/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/react-router";
import { App } from "./App";

// Get Clerk publishable key from environment
// In development, Bun injects it as window.__CLERK_PUBLISHABLE_KEY__
// In production, it's available via import.meta.env (from build.ts define)
function getWindowKey() {
  if (typeof window === "undefined") return undefined;
  // Use bracket notation to access dynamic property
  // @ts-ignore - accessing dynamic property on window
  return window["__CLERK_PUBLISHABLE_KEY__"];
}

const PUBLISHABLE_KEY = 
  getWindowKey() ||
  import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY || 
  import.meta.env?.BUN_PUBLIC_CLERK_PUBLISHABLE_KEY ||
  (typeof process !== "undefined" && process.env?.VITE_CLERK_PUBLISHABLE_KEY) ||
  (typeof process !== "undefined" && process.env?.BUN_PUBLIC_CLERK_PUBLISHABLE_KEY);

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key. Please set VITE_CLERK_PUBLISHABLE_KEY or BUN_PUBLIC_CLERK_PUBLISHABLE_KEY in your .env file");
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <BrowserRouter>
      <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
        <App />
      </ClerkProvider>
    </BrowserRouter>
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}
